/**
 * The ONE shared state of the booking blocks (CHANGE 2026-10-18 §1.2) and the user actions. Blocks never call
 * the API: they render from this state and call these actions. This is the only module that talks to the API
 * client, so the call budget (§1.4) is enforced here.
 *
 * No business logic (invariant 1): valid dates, prices, allocation and explanations come from the API; this
 * module only stores answers and the guest's choices.
 */
import { addDays, addMonths, isDate, isMonth, nightsBetween } from './dates.js';

const QUOTE_DEBOUNCE_MS = 250;

/**
 * The search kept in the browser (resume-search change of 19 Oct 2026): across a language switch (each language is a
 * separate page) and for a later visit. Not personal (criteria, selection, step); never sent to the server, never in
 * the URL. The details-step draft (personal) stays in sessionStorage (checkout.js).
 */
const SAVED_KEY = 'pap.search.v1';
const SAVED_MAX_AGE = 30 * 24 * 3600 * 1000; // older: deleted, the page starts as today
const RETURN_AFTER = 30 * 60 * 1000; // younger: a language switch (the step comes back, no note); older: a return visit
/**
 * URL parameters of the stay a link can carry (compact search, TranslatePress switcher), saved as fromParams. Not
 * room_id (1.7.0): the requested room is read from the URL on every load and never saved (applyParams). None may
 * be a WordPress query variable: WordPress parses the query string before the page runs (?chambre=X asked for the
 * `chambre` post X: a 404, hence room_id, 1.6.2). bin/check-params checks them all on the live site.
 */
const URL_KEYS = ['arrivee', 'depart', 'nuits', 'adultes', 'enfants', 'mois'];

function storage() {
	try {
		return window.localStorage;
	} catch (_e) {
		return null; // blocked: the page works as before
	}
}

function readSaved() {
	try {
		const saved = JSON.parse(storage()?.getItem(SAVED_KEY) || 'null');
		return saved && saved.v === 1 ? saved : null;
	} catch (_e) {
		return null;
	}
}

function forgetSaved() {
	try {
		storage()?.removeItem(SAVED_KEY);
	} catch (_e) {
		// nothing to forget
	}
}

/** The listed URL parameters present, as {key: value} (compared with the saved fromParams). */
function urlParams(params) {
	const out = {};
	for (const key of URL_KEYS) {
		if (params.has(key)) {
			out[key] = params.get(key);
		}
	}
	return out;
}

const sameParams = (a, b) => JSON.stringify(Object.entries(a || {}).sort()) === JSON.stringify(Object.entries(b || {}).sort());
/** Matomo through the API client (api.js `analytics`, 1.6.0); a client without it (a test double) sends nothing. */
const NO_ANALYTICS = { track() {}, forbid() {}, mark: () => 'GENERIC' };

/** French count word for the Matomo names (fixed words, never translated): n > 1 → plural, as gettext's French rule. */
const count = (n, one, many) => n + ' ' + (n > 1 ? many : one);

/**
 * Matomo `recherche` name of one searched stay (1.8.1), from the server's `stay` echo and the bookable count —
 * formatting only: « ok · 2027-03-08 · 3 nuits · 2 adultes · 1 enfant » (« aucune-dispo · … » when nothing is
 * bookable). Month, nights or party totals come from Matomo segments (event name contains `2027-03`, `5 nuits`).
 */
export function searchName(stay, bookable) {
	const children = (stay.childrenAges || []).length;
	return [bookable > 0 ? 'ok' : 'aucune-dispo', stay.arrival, count(stay.nights, 'nuit', 'nuits'), count(stay.adults, 'adulte', 'adultes'), count(children, 'enfant', 'enfants')].join(' · ');
}

/**
 * Matomo `resultat` name (1.8.1): the stay-level reason first (`sejour:<code>`, when nothing is bookable), then every
 * room in the server's order, `<slug>:disponible` or `<slug>:<reason code>`, joined with « · ». slugOf gives the
 * WordPress slug — never the Beds24 ID; a room without a WordPress page has no slug: left out.
 */
export function resultName(body, slugOf) {
	const parts = body.summary?.code ? ['sejour:' + body.summary.code] : [];
	for (const room of body.rooms || []) {
		const slug = slugOf(room.roomId);
		if (slug) {
			parts.push(slug + ':' + (room.bookable ? 'disponible' : room.reason?.code || 'UNKNOWN'));
		}
	}
	return parts.join(' · ');
}
const DEFAULT_MAX_GUESTS = 10; // until /start answers (compact block before the picker opens)

/**
 * Once both dates are chosen, a click on the calendar starts a new selection (Booking.com style).
 * Jacques may revisit this (CHANGE §2.1): false = a click on a later valid departure moves the departure.
 */
export const RESTART_ON_COMPLETE = true;

/**
 * @param {{api: Object, today: string, calendarMonths?: number, month?: ?string}} options
 *   month = the URL parameter `mois` ("YYYY-MM", else ignored): first month of the calendars on the page (0.9.6).
 */
export function createBooking({ api, today, month = null }) {
	const analytics = api.analytics || NO_ANALYTICS;
	const { track, forbid } = analytics;
	const listeners = new Set();
	const state = {
		today,
		maxDate: null, // the calendar window's end, from the server (Phase 3: never computed here)
		view: 'search', // search | summary (switch off) | details | retry (0.10.0); results show as soon as a search exists
		stepRestored: false, // the details step was reopened by the page (saved search), not chosen: no Matomo `details` (1.8.1)
		retry: null, // {ref, token}: back from Stripe without paying (?paiement=annule, 0.10.0)
		resume: null, // {savedAt, returning, lost}: a saved search restored on load (resume-search change, 0.10.1)
		panelRequest: 0, // incremented by « Nouvelle recherche »: the search bar opens its Dates panel
		started: false,
		loaded: false,
		error: null, // load error response
		config: null,
		rooms: [],
		days: {},
		minStay: null, // {from, to}: room minimum stays over the window (/calendar), for the legend
		minStayByMonth: {}, // "YYYY-MM" → {from, to} (0.9.2): the legend follows the months on screen
		arrival: null,
		departure: null,
		departures: [],
		departuresFor: null, // arrival the departures belong to (null while loading)
		minNights: null,
		firstDeparture: null, // from /calendar/departures (Phase 3): the page compares dates, never adds nights
		minStayByWindow: {}, // "YYYY-MM" → {from, to} for that month and the next (two months on screen)
		adults: 2,
		childrenAges: [],
		promo: '',
		searching: false,
		search: null, // last /search response {status, body}
		searchedFor: null, // the form as sent with that search {arrival, departure, adults, childrenAges, promo} (0.9.7)
		reveal: true, // the results block scrolls to the results and focuses their title when this search ends (0.9.9)
		chosen: {}, // roomId → {offerId, occupancy}
		order: [], // roomIds in the order chosen
		shown: {}, // roomId → occupancy picked with the 2 / 3 switch
		quote: null,
		quoting: false,
		room: null, // the requested room (URL param room_id, 1.7.0): sent with each search, never saved (pap.search.v1)
		month: isMonth(month) ? month : null // URL param mois: first calendar month (after arrivee, before the shortcode's mois)
	};
	let loading = null;
	let searchSeq = 0; // the answer of an older /search is ignored
	let fromParams = {}; // URL parameters applied in this tab, saved with the search
	let restoreDetails = false; // reopen the details step once the restored selection is quoted (language switch)
	// Auto-continue (1.5.0): the selection a « Choisir » (or Océane's switch on a chosen room) just made; when ITS quote
	// is OK the page continues by itself. Never a restored or re-chosen selection, never after an unchoose.
	let autoFor = null;
	let cameBack = false; // the guest left the details step for the results: « Continuer » only, until « Nouvelle recherche »
	let saving = true;

	/** Saves the searched criteria, the selection and the step (after a search, a selection change, a step change). */
	function save() {
		if (!saving || !state.searchedFor) {
			return;
		}
		const f = state.searchedFor;
		try {
			storage()?.setItem(SAVED_KEY, JSON.stringify({
				v: 1,
				arrival: f.arrival,
				departure: f.departure,
				adults: f.adults,
				childrenAges: f.childrenAges,
				promo: f.promo,
				selection: selection(),
				step: state.view === 'details' ? 'details' : 'results',
				savedAt: Date.now(),
				fromParams
			}));
		} catch (_e) {
			// storage full or blocked: nothing kept, the page works as today
		}
	}

	function notify() {
		for (const fn of listeners) {
			fn(state);
		}
	}

	function set(patch) {
		Object.assign(state, patch);
		notify();
	}

	const guests = () => state.adults + state.childrenAges.length;

	// ------------------------------------------------------------ load (2 requests: /start, /session + calendar)

	/** Starts the page data once (lazy for a compact-only page). Resolves when loaded. */
	function start() {
		if (!loading) {
			state.started = true;
			notify();
			loading = api.load(today).then((r) => {
				if (r.status !== 200) {
					set({ loaded: true, error: r });
				} else {
					set({ loaded: true, config: r.body.config, rooms: r.body.rooms || [], days: r.body.days || {}, maxDate: r.body.to ?? null, minStay: r.body.minStay ?? null, minStayByMonth: r.body.minStayByMonth || {}, minStayByWindow: r.body.minStayByWindow || {} });
				}
				return state;
			});
		}
		return loading;
	}

	// ------------------------------------------------------------ party

	function maxGuests() {
		return state.config?.party?.maxGuests ?? DEFAULT_MAX_GUESTS;
	}

	function setAdults(n) {
		state.adults = Math.max(1, Math.min(n, maxGuests() - state.childrenAges.length));
		partyChanged();
	}

	function setChildren(n) {
		const count = Math.max(0, Math.min(n, maxGuests() - state.adults));
		state.childrenAges = count > state.childrenAges.length
			? [...state.childrenAges, ...Array(count - state.childrenAges.length).fill(null)]
			: state.childrenAges.slice(0, count);
		partyChanged();
	}

	function setAge(index, age) {
		state.childrenAges = state.childrenAges.map((a, i) => (i === index ? age : a));
		notify();
	}

	const agesSet = () => state.childrenAges.every((age) => age !== null);

	const hintsSeen = new Set(); // party hint codes already sent to Matomo on this page

	/**
	 * The dates do not depend on the party (0.9.4): they are kept, departures are not asked again. No search either:
	 * a search starts only on a deliberate action (search bar 0.9.9: closing Voyageurs, « Valider »); results on
	 * screen become stale.
	 */
	function partyChanged() {
		notify();
	}

	/**
	 * Matomo (1.6.0): after the guest's own change (not the URL or a restored search), the capacity hint the guest
	 * selector shows (/config party.hints), once per code per page.
	 */
	function partyHintEvent() {
		const code = state.config?.party?.hints?.[guests()]?.code;
		if (code && !hintsSeen.has(code)) {
			hintsSeen.add(code);
			track('voyageurs-alerte', code);
		}
	}

	/** The form as a search would send it (compared with searchedFor to tell stale results, 0.9.7). */
	const formNow = () => ({ arrival: state.arrival, departure: state.departure, adults: state.adults, childrenAges: [...state.childrenAges], promo: state.promo.trim() });

	/**
	 * Results on screen no longer match the form (dates, party, an age or the promo changed since the search): shown
	 * muted, no rate can be chosen, the bar is hidden, until the change is confirmed (a new search). Putting the form
	 * back as searched makes them current again.
	 */
	function isStale() {
		return Boolean(state.search && state.searchedFor) && JSON.stringify(formNow()) !== JSON.stringify(state.searchedFor);
	}

	/**
	 * Why the results are stale (0.9.9), for the note: 'departure' (arrival chosen, no departure yet), 'ages' (a
	 * child's age missing), 'changed' (anything else); null when they are current.
	 */
	function staleReason() {
		if (!isStale()) {
			return null;
		}
		if (state.arrival && !state.departure) {
			return 'departure';
		}
		return agesSet() ? 'changed' : 'ages';
	}

	// ------------------------------------------------------------ dates

	/** What a click on a date does now: 'arrival' | 'departure' | 'reset' | null. */
	function pickAction(date) {
		if (!state.loaded || !isDate(date) || date < today || date > state.maxDate) {
			return null;
		}
		// Selectable = Beds24 has the night available (`closed: false`, 01 §19) — never a hint (reason).
		const canArrive = state.days[date]?.closed === false;
		const choosingDeparture = state.arrival && !state.departure;
		if (!choosingDeparture) {
			if (state.arrival && state.departure && !RESTART_ON_COMPLETE && date > state.arrival && state.departures.includes(date)) {
				return 'departure';
			}
			return canArrive ? 'arrival' : null;
		}
		if (date === state.arrival) {
			return 'reset';
		}
		if (date < state.arrival) {
			return canArrive ? 'arrival' : null; // a date before the arrival restarts the selection
		}
		if (state.departuresFor !== state.arrival) {
			return null; // departures still loading
		}
		// A departure the server sent: over nights Beds24 has available (1.4.3), from firstDeparture on (1.7.3, no 1 night).
		return state.departures.includes(date) ? 'departure' : null;
	}

	let calendarSent = false;
	let datesSent = false;

	/**
	 * Matomo `calendrier-ouvert` (1.8.0): the guest's first use of a date picker on this page (open, day, month arrow);
	 * sent once the page data is in (a click on a calendar still loading counts too).
	 */
	function calendarUsed() {
		if (!calendarSent) {
			calendarSent = true;
			Promise.resolve(loading).then(() => track('calendrier-ouvert'));
		}
	}

	function pick(date) {
		switch (pickAction(date)) {
			case 'arrival':
				return chooseArrival(date);
			case 'departure':
				if (!datesSent) {
					datesSent = true;
					track('dates-choisies'); // Matomo (1.8.0): the guest's first arrival + departure in the calendar
				}
				return chooseStay(state.arrival, date);
			case 'reset':
				return clearDates();
			default:
				return undefined;
		}
	}

	async function loadDepartures(arrival) {
		const r = await api.departures(arrival); // independent of the party (0.9.4)
		if (state.arrival !== arrival) {
			return;
		}
		set({
			departures: r.status === 200 ? r.body.departures : [],
			minNights: r.status === 200 ? r.body.minNights ?? null : null,
			firstDeparture: r.status === 200 ? r.body.firstDeparture ?? null : null,
			departuresFor: arrival
		});
	}

	async function chooseArrival(date) {
		set({ arrival: date, departure: null, departures: [], departuresFor: null, minNights: null, firstDeparture: null });
		await loadDepartures(date);
	}

	/** Both dates (a departure click, or a suggestion). Beds24 decides at /search: no stay check (0.9.4). */
	async function chooseStay(arrival, departure) {
		const sameArrival = state.arrival === arrival;
		set(sameArrival ? { departure } : { arrival, departure, departures: [], departuresFor: null, minNights: null, firstDeparture: null });
		if (state.departuresFor !== arrival) {
			await loadDepartures(arrival);
		}
	}

	function clearDates() {
		set({ arrival: null, departure: null, departures: [], departuresFor: null, minNights: null, firstDeparture: null });
	}

	/**
	 * A suggestion from the server (nearby dates, in the results summary): sets the dates and searches (0.9.9).
	 * type: the server's suggestion type (`LATER_DEPARTURE` / `EARLIER_ARRIVAL`), Matomo `suggestion` (1.8.0).
	 */
	async function chooseSuggestion(arrival, departure, type) {
		track('suggestion', type);
		const dates = chooseStay(arrival, departure);
		await Promise.all([dates, searchIfChanged()]);
	}

	/** Typing in the promo field: re-render only when the results flip between current and stale. */
	function setPromo(value) {
		const before = isStale();
		state.promo = value;
		if (isStale() !== before) {
			notify();
		}
	}

	/** Loaded, both dates chosen, every child's age set. No stay check: Beds24 decides at /search (0.9.4). */
	function canSearch() {
		return state.loaded && !state.error && Boolean(state.arrival) && Boolean(state.departure) && agesSet();
	}

	// ------------------------------------------------------------ search, basket, summary

	async function runSearch() {
		if (!canSearch()) {
			return;
		}
		await search();
	}

	/** The form can be searched and differs from the last search (or there is none yet): a search would change something. */
	function needsSearch() {
		return canSearch() && (!state.searchedFor || JSON.stringify(formNow()) !== JSON.stringify(state.searchedFor));
	}

	/**
	 * Search bar (0.9.9): a deliberate action (departure picked, Voyageurs or Code promo closed, « Valider »,
	 * « Appliquer », a suggestion) searches only when the form changed. reveal = false: the results block neither
	 * scrolls nor takes the focus when it ends (a panel stays open, or Escape put the focus back on its tab).
	 * @returns {boolean} A search started.
	 */
	function searchIfChanged({ reveal = true } = {}) {
		if (!needsSearch()) {
			return false;
		}
		search({ reveal });
		return true;
	}

	/**
	 * POST /search for the current stay and party (the caller decides when; runSearch() checks the form).
	 * keep (0.10.0): rooms to choose again once the answer is in — [{roomId, offerId, occupancy}] — when the new
	 * results still offer that rate (at that occupancy, when one was picked); then a new quote.
	 * guest (1.8.1): the guest started it (« Rechercher », a search-bar change, a suggestion, a link this tab has not
	 * applied yet) — only then the Matomo events; false for the page's own searches (restore, 409).
	 */
	async function search({ reveal = true, keep = null, guest = true } = {}) {
		const seq = ++searchSeq;
		const sent = formNow();
		forbid(sent.promo); // never in an event (Matomo, 1.6.0)
		autoFor = null;
		set({ searching: true, view: 'search', search: null, searchedFor: sent, reveal, chosen: {}, order: [], shown: {}, quote: null });
		save();
		// The requested room (1.7.0): the server puts it first and echoes it in `requested` (unknown → ignored there).
		const r = await api.search({ ...sent, childrenAges: [...sent.childrenAges], ...(state.room ? { requestedRoom: state.room } : {}) });
		if (seq !== searchSeq) {
			return; // a newer search (party or dates changed) is on its way
		}
		state.search = r;
		set({ searching: false });
		if (guest) {
			searchEvents(r);
		}
		if (keep?.length && r.status === 200) {
			chooseAgain(keep, r.body.rooms || []);
		}
	}

	/**
	 * Matomo, once per search the guest started (1.8.1, docs/handoff/customisation.md § 5): `recherche` (criteria and
	 * answer in one name, value = bookable rooms), `resultat` (every room's answer, Détaillé), then the requested room
	 * and the promo answer (never the code itself). A search the page runs itself (restored, 409) sends nothing. An
	 * error answer: `erreur` from errorFor().
	 */
	function searchEvents(r) {
		if (r.status !== 200) {
			return;
		}
		const slugOf = (roomId) => state.rooms.find((c) => c.roomId === roomId)?.slug;
		const bookable = (r.body.rooms || []).filter((room) => room.bookable).length;
		if (r.body.stay) {
			track('recherche', searchName(r.body.stay, bookable), bookable);
		}
		const result = resultName(r.body, slugOf);
		if (result) {
			track('resultat', result);
		}
		// The requested room (1.7.0), by its WordPress slug.
		const asked = r.body.requested;
		const slug = asked ? slugOf(asked.roomId) : null;
		if (slug) {
			track('chambre-demandee', (asked.bookable ? 'disponible' : 'indisponible') + ':' + slug);
		}
		const promo = r.body.promo;
		if (promo) {
			track('code-promo', promo.valid ? 'valide' + (promo.label?.code ? '-' + promo.label.code : '') : promo.reason === 'PROMO_UNAVAILABLE_ONLINE' ? 'pas-en-ligne' : 'inconnu');
		}
	}

	/** Re-selects the kept rates the new results still offer (search keep, 0.10.0). @returns {boolean} all of them */
	function chooseAgain(keep, rooms) {
		autoFor = null; // re-chosen by the page (saved search, 409), not by the guest
		let all = true;
		for (const k of keep) {
			const room = rooms.find((r) => r.roomId === k.roomId && r.bookable);
			const offered = (room?.occupancyOptions || []).some((o) => (k.occupancy === null || o.occupancy === k.occupancy) && o.rates.some((rate) => rate.offerId === k.offerId));
			if (!offered) {
				all = false;
				continue;
			}
			state.chosen[k.roomId] = { offerId: k.offerId, occupancy: k.occupancy };
			if (k.occupancy !== null) {
				state.shown[k.roomId] = k.occupancy;
			}
			state.order.push(k.roomId);
		}
		if (state.order.length) {
			selectionChanged();
		}
		return all;
	}

	let quoteTimer = null;
	function selectionChanged() {
		save();
		notify();
		clearTimeout(quoteTimer);
		if (state.order.length === 0) {
			set({ quote: null, quoting: false });
			return;
		}
		quoteTimer = setTimeout(runQuote, QUOTE_DEBOUNCE_MS);
	}

	const selection = () => state.order.map((roomId) => ({ roomId, offerId: state.chosen[roomId].offerId, occupancy: state.chosen[roomId].occupancy }));

	async function runQuote() {
		const sent = selection();
		set({ quoting: true });
		const r = await api.quote({ searchToken: state.search.body.searchToken, selection: sent });
		if (JSON.stringify(sent) !== JSON.stringify(selection())) {
			return; // a newer selection is on its way
		}
		const auto = autoFor === JSON.stringify(sent);
		autoFor = null;
		set({ quoting: false, quote: r });
		if (restoreDetails) {
			restoreDetails = false;
			showSummary({ restored: true }); // back on the details step after a language switch (only when it can continue)
		} else if (auto && state.view === 'search') {
			showSummary(); // the « Choisir » that completed the selection: what « Continuer » does (only when it can continue)
		}
	}

	/** After a guest's choice: its quote may continue by itself (not once the guest came back from the details step). */
	function armAuto(arm) {
		autoFor = arm && !cameBack ? JSON.stringify(selection()) : null;
	}

	function toggleRate(roomId, offerId) {
		if (isStale()) {
			return; // stale results: nothing can be chosen until the change is confirmed (0.9.7)
		}
		const current = state.chosen[roomId];
		const unchoose = Boolean(current && current.offerId === offerId);
		if (unchoose) {
			delete state.chosen[roomId];
			state.order = state.order.filter((id) => id !== roomId);
		} else {
			state.chosen[roomId] = { offerId, occupancy: state.shown[roomId] ?? null };
			if (!state.order.includes(roomId)) {
				state.order.push(roomId);
			}
		}
		if (!unchoose) {
			track('tarif-choisi', rateSlug(roomId, offerId));
		}
		armAuto(!unchoose);
		selectionChanged();
	}

	/** Matomo `tarif-choisi` (1.8.0): the rate's `slug` from /search (the Beds24 internal name, `flex-30`), never the offer number. */
	function rateSlug(roomId, offerId) {
		const room = (state.search?.body?.rooms || []).find((r) => r.roomId === roomId);
		for (const option of room?.occupancyOptions || []) {
			const rate = option.rates.find((x) => x.offerId === offerId);
			if (rate) {
				return rate.slug || undefined;
			}
		}
		return undefined;
	}

	/** Océane "2 / 3 personnes": once used, the choice is sent as occupancy; untouched, the server allocates. */
	function setOccupancy(roomId, occupancy) {
		if (isStale()) {
			return;
		}
		state.shown[roomId] = occupancy;
		if (state.chosen[roomId]) {
			state.chosen[roomId].occupancy = occupancy;
			armAuto(true);
			selectionChanged();
		} else {
			notify();
		}
	}

	function canContinue() {
		const q = state.quote;
		return q?.status === 200 && q.body.allocation.status === 'OK' && !state.quoting;
	}

	/** The online booking switch for this visitor (/start config.booking.enabled, 0.10.0). */
	function bookingEnabled() {
		return Boolean(state.config?.booking?.enabled);
	}

	/** « Continuer »: « Vos coordonnées et paiement » when the switch is on, else today's summary. */
	function showSummary({ restored = false } = {}) {
		if (canContinue()) {
			if (bookingEnabled() && !restored && state.view !== 'details') {
				track('chambres', undefined, state.order.length);
			}
			set({ view: bookingEnabled() ? 'details' : 'summary', stepRestored: restored });
			save();
		}
		return state.view;
	}

	/** Back to the results; from the details step (any route: « Modifier la sélection », ‹, Back, 409) → came back. */
	function backToResults() {
		if (state.view === 'details') {
			cameBack = true;
		}
		set({ view: 'search' });
		save();
	}

	/** A booking was created (201): nothing to resume any more. */
	function forgetSearch() {
		forgetSaved();
		saving = false; // the page is leaving (Stripe or « merci »)
	}

	/**
	 * « Nouvelle recherche » of the resume note: the saved search is deleted, the form back to its defaults (2 adults,
	 * no dates, no promo), the restored results and selection cleared; the search bar opens its Dates panel.
	 */
	function newSearch() {
		forgetSaved();
		searchSeq++;
		clearTimeout(quoteTimer);
		cameBack = false;
		autoFor = null;
		set({
			arrival: null, departure: null, departures: [], departuresFor: null, minNights: null, firstDeparture: null,
			adults: 2, childrenAges: [], promo: '',
			searching: false, search: null, searchedFor: null, chosen: {}, order: [], shown: {}, quote: null, quoting: false,
			view: 'search', resume: null, panelRequest: state.panelRequest + 1
		});
	}

	/** Back from Stripe without paying (0.10.0): the retry view for that booking, nothing searched. */
	function showRetry(ref, token) {
		forbid(ref); // never in an event; the token is caught by the safety filter's token pattern
		track('retour-stripe', 'annule');
		set({ view: 'retry', retry: { ref, token } });
	}

	/** 409 PRICE_CHANGED: the fresh quote the server sent replaces the current one (« Votre séjour », amounts). */
	function replaceQuote(body) {
		set({ quote: { status: 200, body } });
	}

	/**
	 * « Revenir aux chambres » after a 409 (0.10.0, Jacques 19 Oct 2026): back to the results and the same search
	 * again; the rooms still offered stay chosen (dropRoomId = the room just booked by someone else).
	 */
	function searchAgainKeeping(dropRoomId = null) {
		const keep = selection().filter((sel) => sel.roomId !== dropRoomId);
		state.view = 'search';
		return search({ keep, guest: false }); // the same search again: no Matomo event
	}

	// ------------------------------------------------------------ checkout calls (the API stays behind the store)

	const bookingProof = () => api.bookingProof();
	const book = (body, key) => api.bookings(body, key);
	const bookingStatus = (ref, token) => api.bookingStatus(ref, token);
	const bookingPayment = (body) => api.bookingPayment(body);

	// ------------------------------------------------------------ URL parameters (compact search → booking page)

	/**
	 * Preselects arrivee, depart (or nuits), adultes, enfants, room_id. Children's ages are not in the URL: the guest
	 * picks them. After the data loads (/start, then /session with the calendar — the only real dependencies: the
	 * session needs the challenge, the calls below need the session cookie), two calls go out IN PARALLEL: none
	 * needs the other's answer. /calendar/departures fills the calendar; /search (when autoSearch and no age is
	 * missing) gets the rooms — Beds24 decides there, and a refused stay comes back explained with every room
	 * (0.7.4; no /stay-check since 0.9.4).
	 * @param {URLSearchParams} params
	 * @param {{autoSearch?: boolean}} opts
	 */
	async function applyParams(params, { autoSearch = false } = {}) {
		// Resume-search change: the URL wins, unless it is the one this tab already applied (a TranslatePress switcher
		// link copies the query string of the page as it was loaded) — then the saved search, newer, wins.
		const fromUrl = urlParams(params);
		// The requested room (1.7.0) is the room_id of the URL the page was entered with — whichever wins below (the
		// URL or the saved search, which never holds it): kept across a language switch or a reload (same URL),
		// cleared by entering without it. Unknown rooms are ignored by the server (/search `requested`).
		const asked = Number(params.get('room_id')); // the Beds24 room ID
		state.room = Number.isInteger(asked) && asked > 0 ? asked : null;
		let saved = autoSearch && state.view === 'search' ? readSaved() : null;
		if (saved && !(saved.arrival > today && Date.now() - saved.savedAt < SAVED_MAX_AGE)) {
			forgetSaved(); // arrival past, or older than 30 days
			saved = null;
		}
		if (saved && (Object.keys(fromUrl).length === 0 || sameParams(fromUrl, saved.fromParams))) {
			fromParams = saved.fromParams || {};
			return restore(saved);
		}
		fromParams = fromUrl;
		const adults = Number(params.get('adultes'));
		const children = Number(params.get('enfants'));
		if (Number.isInteger(adults) && adults >= 1) {
			state.adults = adults;
		}
		if (Number.isInteger(children) && children > 0) {
			state.childrenAges = Array(children).fill(null);
		}
		const arrival = params.get('arrivee');
		let departure = params.get('depart');
		const nights = Number(params.get('nuits'));
		if (!isDate(departure) && isDate(arrival) && Number.isInteger(nights) && nights > 0) {
			departure = addDays(arrival, nights);
		}
		await start();
		setAdults(state.adults); // clamp to the real capacity
		if (state.error || !isDate(arrival) || pickAction(arrival) !== 'arrival') {
			return;
		}
		const stay = isDate(departure) && departure > arrival && departure <= state.maxDate ? departure : null;
		set({ arrival, departure: stay, departures: [], departuresFor: null, minNights: null, firstDeparture: null });
		await Promise.all([
			loadDepartures(arrival),
			stay && autoSearch && agesSet() ? search() : null
		]);
	}

	/** Restores a saved search: the criteria, the same search as the URL preselection, the selection, the step. */
	async function restore(saved) {
		const age = Date.now() - saved.savedAt;
		state.adults = saved.adults;
		state.childrenAges = [...(saved.childrenAges || [])];
		state.promo = saved.promo || '';
		await start();
		setAdults(state.adults);
		if (state.error || pickAction(saved.arrival) !== 'arrival') {
			return;
		}
		const departure = saved.departure > saved.arrival && saved.departure <= state.maxDate ? saved.departure : null;
		set({ arrival: saved.arrival, departure, departures: [], departuresFor: null, minNights: null, firstDeparture: null });
		if (!departure || !agesSet()) {
			await loadDepartures(saved.arrival);
			return;
		}
		const keep = (saved.selection || []).map((k) => ({ roomId: k.roomId, offerId: k.offerId, occupancy: k.occupancy ?? null }));
		restoreDetails = saved.step === 'details' && age < RETURN_AFTER && keep.length > 0;
		const seq = searchSeq + 1;
		await Promise.all([loadDepartures(saved.arrival), search({ keep, reveal: false, guest: false })]);
		if (seq !== searchSeq) {
			restoreDetails = false;
			return;
		}
		const lost = keep.length > 0 && keep.some((k) => !state.order.includes(k.roomId));
		if (lost) {
			restoreDetails = false; // the selection is not complete: stay on the results
		}
		set({ resume: { savedAt: saved.savedAt, returning: age >= RETURN_AFTER, lost } });
	}

	return {
		state,
		analytics, // for the checkout chunk (Matomo, 1.6.0)
		subscribe(fn) {
			listeners.add(fn);
			return () => listeners.delete(fn);
		},
		start,
		guests,
		maxGuests,
		setAdults: (n) => {
			setAdults(n);
			partyHintEvent();
		},
		setChildren: (n) => {
			setChildren(n);
			partyHintEvent();
		},
		setAge,
		pickAction,
		pick,
		calendarUsed,
		clearDates,
		chooseSuggestion,
		setPromo,
		canSearch,
		needsSearch,
		isStale,
		staleReason,
		runSearch,
		searchIfChanged,
		toggleRate,
		setOccupancy,
		canContinue,
		bookingEnabled,
		showSummary,
		backToResults,
		showRetry,
		replaceQuote,
		searchAgainKeeping,
		selection,
		forgetSearch,
		newSearch,
		bookingProof,
		book,
		bookingStatus,
		bookingPayment,
		applyParams,
		nights: () => (state.arrival && state.departure ? nightsBetween(state.arrival, state.departure) : 0)
	};
}
